<template>
    <VModal name="compose-message" :width="500" height="auto" scrollable close-button @closed="clearForm">
        <div class="composer-modal">
            <h2 class="composer-modal__title">
                {{ postingAsStaff ? $t('POST_AS_DEVELOPER_STAFF') : $t('POST_COMMENT') }}
            </h2>

            <div v-if="replyingTo && replyingTo.id" class="composer-modal__replying-to">
                <div>{{ $t('REPLYING_TO') }}: <span>{{ replyingTo.name }}</span></div>
                <div @click="cancelReply" class="composer-modal__replying-to-cancel">{{ $t('CANCEL') }}</div>
            </div>

            <div class="composer-modal__form">
                <VTextarea v-model="message" :placeholder="$t('WRITE_YOUR_COMMENT_HERE')"
                    class="composer-modal__textarea" rows="6" ref="messageTextarea" @paste.native="handlePaste" />

                <div class="composer-modal__form-notice">
                    <template v-if="isCustomerMode">
                        <template v-if="isPrivate">
                            {{ $t('PRIVATE_COMMENT_NOTICE') }}
                        </template>
                        <template v-else>
                            {{ $t('PUBLIC_COMMENT_NOTICE') }}
                        </template>
                    </template>
                </div>

                <div class="composer-modal__form-row">
                    <div class="composer-modal__form-actions">
                        <div @click="attachScreenshot" class="composer-modal__form-action">
                            <img src="@/assets/attachment.svg" alt="Attach" />
                            <span>{{ $t('SCREENSHOT') }}</span>
                        </div>
                        <!-- <div @click="requestSupport" class="composer-modal__form-action">
                            <img src="@/assets/support.svg" alt="Support" />
                            <span>{{ $t('REQUEST_STAFF_SUPPORT') }}</span>
                        </div> -->
                    </div>

                    <div class="composer-modal__form-private" v-if="canPostAsStaff && forceType === null">
                        <Checkbox :value="isPrivateReply" @input="isPrivateReply = $event">
                            {{ $t('REPLY_PRIVATELY') }}
                        </Checkbox>
                    </div>
                </div>
            </div>

            <div class="composer-modal__footer">
                <Button @click="cancel" type="secondary">{{ $t('CANCEL') }}</Button>
                <Button @click="submitMessage" :disabled="!message.trim() || isPostingLocked" type="primary">
                    {{ postingButtonText }}
                </Button>
            </div>
        </div>
        <input ref="fileInput" type="file" accept="image/png,image/jpeg,image/heic" style="display: none"
            @change="handleFileChange" />
    </VModal>
</template>

<script>
import Vue from 'vue';
import VModal from '@/components/VModal.vue';
import Button from '@/components/Button.vue';
import VTextarea from '@/components/VTextarea.vue';
import Checkbox from '@/components/Checkbox.vue';

export default {
  name: 'MessageComposerModal',
  components: {
    VModal,
    Button,
    VTextarea,
    Checkbox,
  },
  props: {
    topic: {
      type: String,
      required: true,
    },
    isPrivate: {
      type: Boolean,
      default: false,
    },
    forceType: {
      type: String,
      default: null,
      validator: value => value === null || value === 'public' || value === 'private',
    },
    isPostingLocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      message: '',
      replyingTo: null,
      isPrivateReply: false,
      fileUploading: false,
      postingAsStaff: false,
      maxImageDimension: 780,
      isProcessingPaste: false,
    };
  },
  computed: {
    isCustomerMode() {
      return !!this.$store.state.customerProfile?.email;
    },
    canPostAsStaff() {
      return !!this.$store.state.publisherProfile?.email;
    },
    postingButtonText() {
      if (this.isPostingLocked) return this.$t('PLEASE_WAIT');
      if (this.fileUploading) return this.$t('UPLOADING');
      return this.postingAsStaff ? this.$t('POST_AS_DEVELOPER_STAFF') : this.$t('POST');
    },
  },
  mounted() {
    // We don't need this anymore - the textarea paste event is sufficient
    // this.$el.addEventListener('paste', this.handlePaste);
  },
  beforeDestroy() {
    // Clean up no longer needed
    // if (this.$el) {
    //     this.$el.removeEventListener('paste', this.handlePaste);
    // }
  },
  methods: {
    clearForm() {
      this.message = '';
      this.replyingTo = null;
      this.isPrivateReply = this.forceType === 'private';
      this.postingAsStaff = false;
    },

    cancel() {
      this.$modal.hide('compose-message');
    },

    openAsUser(replyTo = null) {
      this.postingAsStaff = false;
      this.replyingTo = replyTo;
      this.isPrivateReply = this.forceType === 'private';
      this.$modal.show('compose-message');
      this.focusTextarea();
    },

    openAsStaff(replyTo = null) {
      this.postingAsStaff = true;
      this.replyingTo = replyTo;
      this.isPrivateReply = this.forceType === 'private';
      this.$modal.show('compose-message');
      this.focusTextarea();
    },

    cancelReply() {
      this.replyingTo = null;
    },

    submitMessage() {
      if (!this.message.trim() || this.fileUploading || this.isPostingLocked) return;

      this.$emit('submit-message', {
        message: this.message,
        replyingTo: this.replyingTo,
        isPrivateReply: this.isPrivateReply,
        asStaff: this.postingAsStaff,
      });

      this.$modal.hide('compose-message');
    },

    attachScreenshot() {
      this.$refs.fileInput.click();
    },

    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.fileUploading = true;

      try {
        // Process the file (resize and convert if needed)
        const processedFile = await this.processImageFile(file);
        if (!processedFile) {
          Vue.notify({
            type: 'error',
            text: 'Unsupported image format. Please use PNG, JPEG, or HEIC images.',
          });
          return;
        }

        // Upload the processed file
        const response = await Vue.prototype.$api.uploadFile({
          type: 'support_image',
          file: processedFile,
        });

        if (response) {
          // Insert the image at cursor position or at the end
          const imageMarkdown = `![screenshot](${response})`;
          this.message = `${this.message}\n${imageMarkdown}`;
        }
      } catch (error) {
        console.error('Failed to upload screenshot:', error);
        Vue.notify({
          type: 'error',
          text: this.$t('FAILED_TO_UPLOAD_IMAGE') + (error.message || this.$t('UNKNOWN_ERROR')),
        });
      } finally {
        this.fileUploading = false;
        this.$refs.fileInput.value = '';
      }
    },

    async processImageFile(file) {
      console.log('Processing image file type:', file.type);

      // Check if file is an image - Mac sometimes pastes with 'application/octet-stream'
      if (!file.type.startsWith('image/') && file.type !== 'application/octet-stream') {
        console.error('Not an image file:', file.type);
        return null;
      }

      try {
        // For macOS screenshots, ensure we can process them regardless of their type
        if (file.type === 'application/octet-stream' || file.type === '') {
          console.log('Treating unknown file type as image');
          // We'll attempt to treat as PNG
          const newFile = new File([file], 'screenshot.png', { type: 'image/png' });
          file = newFile;
        }

        // Handle HEIC images - convert to PNG
        if (file.type === 'image/heic' || file.type === 'image/heif'
                    || (file.name && file.name.toLowerCase().endsWith('.heic'))) {
          try {
            console.log('Converting HEIC image');
            // Dynamically import heic2any
            const heic2any = await import('heic2any');
            const blob = await heic2any.default({
              blob: file,
              toType: 'image/png',
            });
            file = new File([blob], file.name ? file.name.replace(/\.(heic|heif)$/i, '.png') : 'screenshot.png',
              { type: 'image/png' });
          } catch (error) {
            console.error('Failed to convert HEIC image:', error);
            throw new Error('Failed to convert HEIC image. Please try another format.');
          }
        }

        // Resize the image if needed
        return await this.resizeImage(file);
      } catch (error) {
        console.error('Error processing image file:', error);
        throw error;
      }
    },

    createImageFromFile(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = e => reject(e);
        img.src = URL.createObjectURL(file);
      });
    },

    async resizeImage(file) {
      try {
        console.log('Resizing image:', file.type, file.name);
        const img = await this.createImageFromFile(file);
        console.log('Image loaded, dimensions:', img.width, 'x', img.height);

        // Check if resizing is needed
        if (img.width <= this.maxImageDimension && img.height <= this.maxImageDimension) {
          console.log('No resize needed');
          // No need to resize, but still convert to PNG for consistency
          if (file.type !== 'image/png') {
            return this.convertToPng(img);
          }
          return file;
        }

        // Calculate new dimensions while maintaining aspect ratio
        let newWidth; let
          newHeight;
        if (img.width > img.height) {
          newWidth = this.maxImageDimension;
          newHeight = Math.floor(img.height * (this.maxImageDimension / img.width));
        } else {
          newHeight = this.maxImageDimension;
          newWidth = Math.floor(img.width * (this.maxImageDimension / img.height));
        }

        console.log('Resizing to:', newWidth, 'x', newHeight);
        return this.convertToPng(img, newWidth, newHeight);
      } catch (error) {
        console.error('Error resizing image:', error);
        // If we fail to resize, still try to use the original file
        return file;
      }
    },

    convertToPng(img, width = null, height = null) {
      return new Promise((resolve, reject) => {
        try {
          // Use the original dimensions if not specified
          const w = width || img.width;
          const h = height || img.height;

          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);

          // Convert to PNG
          canvas.toBlob((blob) => {
            if (!blob) {
              console.error('Failed to create blob from canvas');
              reject(new Error('Failed to create image from canvas'));
              return;
            }

            const filename = `pasted-image-${new Date().getTime()}.png`;
            const resizedFile = new File([blob], filename, { type: 'image/png' });
            console.log('Converted to PNG:', resizedFile.size, 'bytes');
            resolve(resizedFile);
          }, 'image/png', 0.95); // 0.95 quality for PNG compression
        } catch (err) {
          console.error('Error in canvas conversion:', err);
          reject(err);
        }
      });
    },

    handlePaste(event) {
      console.log('Paste event triggered in modal', event);

      // Prevent double processing
      if (this.isProcessingPaste || this.fileUploading) {
        console.log('Already processing an image, ignoring duplicate paste event');
        return;
      }

      // Need to ensure we have proper clipboard data
      if (!event.clipboardData && !window.clipboardData) {
        console.error('No clipboard data available');
        return;
      }

      const clipboardData = event.clipboardData || window.clipboardData;
      const { items } = clipboardData;
      console.log('Clipboard items:', items, clipboardData.types);

      if (!items) {
        console.error('No clipboard items found');
        return;
      }

      // Check if we have image data in the clipboard
      let foundImage = false;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        console.log('Processing item:', item.type, item.kind);

        if (item.type.indexOf('image') === 0) {
          foundImage = true;
          event.preventDefault(); // Prevent default paste behavior

          const file = item.getAsFile();
          console.log('Found image file:', file);

          if (file) {
            this.isProcessingPaste = true; // Set flag to prevent duplicate processing
            this.handlePastedImage(file)
              .finally(() => {
                this.isProcessingPaste = false; // Reset flag when done
              });
          } else {
            console.error('Failed to get file from clipboard item');
          }
          break;
        }
      }

      // If we didn't find an image, let the default paste behavior continue
      if (!foundImage) {
        console.log('No image found in clipboard, continuing with normal paste');
      }
    },

    async handlePastedImage(file) {
      if (!file) {
        console.error('No file received from paste');
        return Promise.resolve(); // Return resolved promise when no file
      }

      console.log('Processing pasted image:', file.type, file.name, file.size);
      this.fileUploading = true;
      Vue.notify({
        type: 'info',
        text: this.$t('PROCESSING_IMAGE'),
      });

      try {
        // Process the pasted image file
        const processedFile = await this.processImageFile(file);
        if (!processedFile) {
          Vue.notify({
            type: 'error',
            text: this.$t('UNSUPPORTED_IMAGE_FORMAT'),
          });
          return Promise.resolve(); // Return resolved promise on error
        }

        console.log('Processed file:', processedFile.type, processedFile.name, processedFile.size);

        // Upload the processed file
        const response = await Vue.prototype.$api.uploadFile({
          type: 'support_image',
          file: processedFile,
        });

        if (response) {
          console.log('Upload successful, inserting markdown:', response);
          // Insert the image at cursor position or at the end
          const imageMarkdown = `![pasted-image](${response})`;
          this.message = `${this.message}\n${imageMarkdown}`;

          Vue.notify({
            type: 'success',
            text: this.$t('IMAGE_UPLOADED_SUCCESSFULLY'),
          });

          return Promise.resolve(response); // Return resolved promise with response
        }
        console.error('Upload returned empty response');
        Vue.notify({
          type: 'error',
          text: this.$t('FAILED_TO_UPLOAD_IMAGE_EMPTY_RESPONSE'),
        });
        return Promise.resolve(); // Return resolved promise on empty response
      } catch (error) {
        console.error('Failed to upload pasted image:', error);
        Vue.notify({
          type: 'error',
          text: this.$t('FAILED_TO_UPLOAD_PASTED_IMAGE') + (error.message || this.$t('UNKNOWN_ERROR')),
        });
        return Promise.reject(error); // Return rejected promise on error
      } finally {
        this.fileUploading = false;
      }
    },

    requestSupport() {
      // Add a tag to request staff support
      // Don't add line breaks if message is empty
      if (this.message.trim()) {
        this.message = `${this.message.trim()}\n\n@staff ${this.$t('PLEASE_HELP_WITH_ISSUE')}`;
      } else {
        this.message = `@staff ${this.$t('PLEASE_HELP_WITH_ISSUE')}`;
      }
    },

    focusTextarea() {
      // Use a small delay to ensure the modal is fully rendered
      setTimeout(() => {
        if (this.$refs.messageTextarea) {
          // Find the actual textarea element inside our component
          const textareaEl = this.$refs.messageTextarea.$el.querySelector('textarea');
          if (textareaEl) {
            textareaEl.focus();
          }
        }
      }, 100);
    },
  },
};
</script>

<style lang="scss" scoped>
.composer-modal {
    &__title {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 15px;
        color: var(--text-primary);
    }

    &__form {
        margin-bottom: 20px;
    }

    &__textarea {
        width: 100%;
        margin-bottom: 10px;
        min-height: 120px;
    }

    &__form-notice {
        font-size: 14px;
        color: var(--text-secondary);
        margin-bottom: 10px;
    }

    &__form-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 15px;
        flex-wrap: wrap;
        gap: 15px;
    }

    &__form-actions {
        display: flex;
        gap: 15px;
    }

    &__form-action {
        display: flex;
        align-items: center;
        gap: 5px;
        cursor: pointer;
        color: var(--primary);
        padding: 5px 8px;
        border-radius: 4px;

        &:hover {
            background-color: var(--bg-secondary, rgba(0, 0, 0, 0.05));
        }

        img {
            width: 16px;
            height: 16px;
            filter: brightness(0) saturate(100%) var(--invert-icon, invert(0%));
        }
    }

    &__replying-to {
        display: flex;
        justify-content: space-between;
        padding: 8px 10px;
        background-color: var(--bg-secondary);
        border-radius: 4px;
        margin-bottom: 12px;
        font-size: 13px;

        span {
            font-weight: 500;
        }

        &-cancel {
            cursor: pointer;
            color: var(--primary);

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 10px;
    }

    &__form-private {
        min-width: 120px;
        padding: 5px 0;
        font-size: 14px;
    }
}
</style>
